import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { explainRegexObject, isValidRegexObject } from '../../utils/regexUtil';
import api from '../../services/api';
import { parseFieldOptions } from '../../utils/dynamicFormUtil';
import SearchableDropdown from '../SearchableDropdown';
import ConditionEditor from './ConditionEditor';

const FormField = ({
  selectedForm,
  editingFieldId,
  setEditingFieldId,
  fields,
  renderDefaultValueOptions,
  projectId,
  forms,
  onSubmit = () => {}
}) => {
  const [fieldName, setFieldName] = useState('');
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [fieldValidationConfig, setFieldValidationConfig] = useState('');
  const [isValidationConfigValid, setIsValidationConfigValid] = useState(null);
  const [validationDescription, setValidationDescription] = useState(null);
  const [fieldPlaceholder, setFieldPlaceholder] = useState('');
  const [isPlaceholderValid, setIsPlaceholderValid] = useState(true);
  const [fieldRequired, setFieldRequired] = useState(true);
  const [fieldDefaultValue, setFieldDefaultValue] = useState(null);
  const [fieldOptions, setFieldOptions] = useState('');
  const [fieldConfig, setFieldConfig] = useState({ maxImage: 1, isShownOnCard: false, storeMapping: null });
  const [storeColumns, setStoreColumns] = useState([]);
  const [condition, setCondition] = useState({ logic: 'AND', groups: [] });
  const [useAsStatistics, setUseAsStatistics] = useState(false);
  const [statisticsMappings, setStatisticsMappings] = useState([]);
  const [useInCoverage, setUseInCoverage] = useState(false);

  useEffect(() => {
    const fetchStoreColumns = async () => {
      const response = await api.get(`/projects/${projectId}/stores/columns`);
      setStoreColumns(response?.data?.columns || []);
    }
    fetchStoreColumns();
  }, []);

  useEffect(() => {
    if (fieldDefaultValue === '') {
      setFieldDefaultValue(null);
    }
  }, [fieldDefaultValue]);

  useEffect(() => {
    if (!editingFieldId) return;
    const field = fields.find(field => field.id === editingFieldId);
    setFieldName(field.name);
    setFieldLabel(field.label);
    setFieldType(field.type);
    setFieldValidationConfig(field.validationConfig);
    setFieldPlaceholder(field.placeholder);
    setIsValidationConfigValid(null);
    setValidationDescription(explainRegexObject(field.validationConfig));
    setIsPlaceholderValid(true);
    setFieldRequired(field.required);
    setFieldDefaultValue(field.defaultValue);
    setFieldOptions(field.options ? field.options.join(',') : '');
    setFieldConfig(field.config);
    setCondition(
      field.condition?.logic && Array.isArray(field.condition.groups)
        ? field.condition
        : { logic: 'AND', groups: [] }
    );
    setUseAsStatistics(field.config?.useAsStatistics || false);
    setStatisticsMappings(field.config?.statisticsMappings || []);
    setUseInCoverage(field.config?.useInCoverage || false);
  }, [editingFieldId]);

  useEffect(() => {
    setFieldName(fieldLabel
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
    );
  }, [fieldLabel]);

  const resetFieldForm = () => {
    setFieldName('');
    setFieldLabel('');
    setFieldType('text');
    setFieldValidationConfig('');
    setFieldPlaceholder('');
    setIsValidationConfigValid(null);
    setValidationDescription(null);
    setIsPlaceholderValid(true);
    setFieldRequired(true);
    setFieldDefaultValue('');
    setFieldOptions('');
    setFieldConfig({ maxImage: 1, readOnly: false, isShownOnCard: false, storeMapping: null });
    setCondition({ logic: 'AND', groups: [] });
    setUseAsStatistics(false);
    setStatisticsMappings([]);
    setUseInCoverage(false);
    setEditingFieldId(null);
  };

  const handleFieldTypeChange = (type) => {
    if (['checkbox', 'label', 'address'].includes(type)) {
      setFieldRequired(false);
    } else {
      setFieldRequired(true);
    }
    setFieldType(type);
  };

  const isFieldNameExists = (forms, fieldName, excludeFieldId = null) => {
    if (!fieldName) return false;
    return forms.some(form =>
      (form.fields || []).some(field =>
        field.name.toLowerCase() === fieldName.toLowerCase() &&
        (!excludeFieldId || field.id !== excludeFieldId)
      )
    );
  };

  const handleAddUpdateField = async (e) => {
    e.preventDefault();
    if (!fieldName || !fieldLabel || !fieldType) return;

    if (fieldValidationConfig && !isValidRegexObject(fieldValidationConfig)) {
      toast.error('Invalid expression');
      return;
    } else if (!isPlaceholderValid) {
      toast.error('Does not pass the validation');
      return;
    } else if (isFieldNameExists(forms, fieldName, editingFieldId)) {
      toast.error('Duplicate field name');
      return;
    }

    const payload = {
      name: fieldName.trim(),
      label: fieldLabel.trim(),
      type: fieldType,
      defaultValue: fieldDefaultValue,
      validationConfig: fieldValidationConfig,
      config: {
        ...fieldConfig,
        useAsStatistics: fieldType === 'dropdown' ? useAsStatistics : false,
        statisticsMappings: fieldType === 'dropdown' ? statisticsMappings : [],
        isShownOnCard: fieldConfig?.isShownOnCard ?? false,
        storeMapping: fieldConfig?.storeMapping || null,
        useInCoverage: fieldType !== 'label' && fieldType !== 'address' ? useInCoverage : false,
      },
      required: fieldRequired,
      placeholder: fieldPlaceholder,
      options: ['dropdown', 'radio'].includes(fieldType) && fieldOptions.trim()
        ? parseFieldOptions(fieldOptions)
        : null,
      condition:
        condition.groups?.some(g => g.conditions?.some(c => c.dependsOnFieldId))
          ? condition
          : null,
    };

    try {
      if (editingFieldId) {
        await api.put(`/projects/${projectId}/forms/${selectedForm.id}/fields/${editingFieldId}`, payload);
      } else {
        await api.post(`/projects/${projectId}/forms/${selectedForm.id}/fields`, payload);
      }
      resetFieldForm();
      onSubmit();
      toast.success('Field updated successfully');
    } catch (err) {
      console.error('Error adding field:', err);
    }
  };

  const validateRegex = (regexInput, testString) => {
    setFieldValidationConfig(regexInput);
    if (regexInput) {
      if (isValidRegexObject(regexInput)) {
        setIsValidationConfigValid(true);
        setValidationDescription(explainRegexObject(regexInput));
        if (testString) {
          try {
            const { pattern, flags } = JSON.parse(regexInput);
            const regex = new RegExp(pattern, flags || '');
            setIsPlaceholderValid(regex.test(testString));
          } catch (error) {
            console.log(error);
          }
        }
      } else {
        setIsValidationConfigValid(false);
        setValidationDescription(null);
      }
    } else {
      setIsValidationConfigValid(null);
      setValidationDescription(null);
    }
  };

  return (
    <Form onSubmit={handleAddUpdateField}>
      <Form.Group className="mb-2">
        <Form.Label>Type</Form.Label>
        <Form.Select value={fieldType} onChange={(e) => handleFieldTypeChange(e.target.value)}>
          <option value="address">Address</option>
          <option value="checkbox">Checkbox</option>
          <option value="counter">Counter</option>
          <option value="currency">Currency</option>
          <option value="date">Date</option>
          <option value="distributor">Distributor</option>
          <option value="dropdown">Dropdown</option>
          <option value="image">Image</option>
          <option value="number">Number</option>
          <option value="radio">Radio</option>
          <option value="store_lookup">Store Lookup</option>
          <option value="text">Text</option>
          <option value="textarea">Textarea</option>
          <option value="label">Title</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label>Label</Form.Label>
        <Form.Control value={fieldLabel} onChange={(e) => setFieldLabel(e.target.value)} required />
      </Form.Group>

      {fieldType !== "label" && (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Store Mapping</Form.Label>
            <Form.Select
              value={fieldConfig.storeMapping?.path || ''}
              onChange={(e) =>
                setFieldConfig({
                  ...fieldConfig,
                  storeMapping: e.target.value
                    ? { path: e.target.value, type: e.target.value === 'address' ? 'address' : undefined }
                    : null
                })
              }
            >
              <option value="">— No Mapping —</option>
              {
              storeColumns.length > 0 ? (
                storeColumns.map((col) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))
              ) : null
              }
            </Form.Select>
          </Form.Group>
          {fieldType === 'image' && (
            <Form.Group className="mb-2">
              <Form.Label>Number of Image(s)</Form.Label>
              <Form.Control
                type="number"
                value={fieldConfig?.maxImage ?? 1}
                onChange={(e) => setFieldConfig({ ...fieldConfig, maxImage: e.target.value })}
              />
            </Form.Group>
          )}

          {['dropdown', 'radio'].includes(fieldType) && (
            <>
              <Form.Group className="mb-2">
                <Form.Label>Options (comma-separated)</Form.Label>
                <Form.Control value={fieldOptions} onChange={(e) => setFieldOptions(e.target.value)} />
              </Form.Group>
              {fieldType === 'dropdown' && (
                <>
                <Form.Check
                  type="checkbox"
                  label="Use as statistics"
                  checked={useAsStatistics}
                  onChange={(e) => setUseAsStatistics(e.target.checked)}
                  className="mt-2"
                />

                {useAsStatistics && fieldOptions && (
                  <div className="mt-3">
                    <Form.Label>Filter by</Form.Label>
                    {statisticsMappings.map((mapping, index) => (
                      <div key={index} className="border rounded p-2 mb-2">
                        <Form.Group className="mb-2">
                          <SearchableDropdown
                            name="provinceIds"
                            label="Options to group"
                            options={fieldOptions
                              .split(',')
                              .map(p => p.trim())
                              .filter(opt => {
                                // Gather all options selected in other mappings
                                const otherSelected = statisticsMappings
                                  .filter((_, i) => i !== index)
                                  .flatMap(m => m.options);

                                return !otherSelected.includes(opt);
                              })
                              .map(p => ({
                                value: p,
                                label: p
                              }))}

                            defaultValue={mapping.options.map(p => ({
                              value: p,
                              label: p
                            }))}
                            onSelect={(option) => {
                              const newMappings = [...statisticsMappings];
                              newMappings[index].options = Array.from(new Set([
                                ...newMappings[index].options,
                                option
                              ]));
                              setStatisticsMappings(newMappings);
                            }}
                            onDelete={(option) => {
                              const newMappings = [...statisticsMappings];
                              newMappings[index].options = newMappings[index].options.filter(opt => !option.includes(opt));
                              setStatisticsMappings(newMappings);
                            }}
                            isMulti={true}
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Label</Form.Label>
                          <Form.Control
                            type="text"
                            value={mapping.label}
                            onChange={(e) => {
                              const newMappings = [...statisticsMappings];
                              newMappings[index].label = e.target.value;
                              setStatisticsMappings(newMappings);
                            }}
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Wildcard</Form.Label>
                          <Form.Control
                            type="text"
                            value={mapping.wildcard}
                            onChange={(e) => {
                              const newMappings = [...statisticsMappings];
                              newMappings[index].wildcard = e.target.value;
                              setStatisticsMappings(newMappings);
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-2">
                          <Form.Label>Badge Color</Form.Label>
                          <Form.Control
                            type="color"
                            value={mapping.color || '#000000'}
                            onChange={(e) => {
                              const newMappings = [...statisticsMappings];
                              newMappings[index].color = e.target.value;
                              setStatisticsMappings(newMappings);
                            }}
                          />
                        </Form.Group>

                        <Form.Check
                          type="checkbox"
                          label="Count in coverage"
                          checked={mapping.countInCoverage || false}
                          onChange={(e) => {
                            const newMappings = [...statisticsMappings];
                            newMappings[index].countInCoverage = e.target.checked;
                            setStatisticsMappings(newMappings);
                          }}
                          className="mb-2"
                        />

                        <Form.Check
                          type="checkbox"
                          label="Hide in Coverage"
                          checked={mapping.hideInCoverage || false}
                          onChange={(e) => {
                            const newMappings = [...statisticsMappings];
                            newMappings[index].hideInCoverage = e.target.checked;
                            setStatisticsMappings(newMappings);
                          }}
                          className="mb-2"
                        />

                        <Button variant="outline-danger" size="sm" onClick={() => {
                          const newMappings = [...statisticsMappings];
                          newMappings.splice(index, 1);
                          setStatisticsMappings(newMappings);
                        }}>Remove</Button>
                      </div>
                    ))}
                    <Button variant="outline-primary" size="sm" onClick={() => setStatisticsMappings([...statisticsMappings, { options: [], label: '', wildcard: '', countInCoverage: false, hideInCoverage: false }])}>
                      + Add Mapping
                    </Button>
                    <br/><br/><br/>
                  </div>
                )}
                </>
              )}
            </>
          )}

          {['text', 'number', 'counter', 'checkbox', 'dropdown', 'radio', 'currency'].includes(fieldType) && (
            <Form.Group className="mb-2">
              <Form.Label>Default Value</Form.Label>
              {renderDefaultValueOptions(fieldType, fieldDefaultValue, setFieldDefaultValue, fieldOptions?.split(','))}
            </Form.Group>
          )}


          {(
            <>
              <Form.Group className="mb-2">
                <Form.Label>Placeholder</Form.Label>
                <Form.Control
                  value={fieldPlaceholder}
                  onChange={(e) => {
                    setFieldPlaceholder(e.target.value);
                    validateRegex(fieldValidationConfig, e.target.value);
                  }}
                  isInvalid={isPlaceholderValid === false}
                />
                <Form.Control.Feedback type="invalid">
                  Does not pass the validation.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Custom Validation</Form.Label>
                <Form.Control
                  value={fieldValidationConfig}
                  placeholder={JSON.stringify({ pattern: '^abc$', flags: 'i' })}
                  onChange={(e) => validateRegex(e.target.value, fieldPlaceholder)}
                  isValid={isValidationConfigValid}
                  isInvalid={isValidationConfigValid === false}
                />
                <Form.Control.Feedback type="valid">
                  {validationDescription}
                </Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Invalid expression
                </Form.Control.Feedback>
              </Form.Group>
            </>
          )}

          {!['checkbox'].includes(fieldType) && (
            <Form.Check
              type="checkbox"
              label="Required"
              checked={fieldRequired}
              onChange={(e) => setFieldRequired(e.target.checked)}
              className="mb-2"
            />
          )}
          <Form.Check
            type="checkbox"
            label="Read Only"
            checked={fieldConfig?.readOnly}
            onChange={(e) => setFieldConfig({ ...fieldConfig, readOnly: e.target.checked })}
            className="mb-2"
          />
          <Form.Check
            type="checkbox"
            label="Show on card"
            checked={fieldConfig?.isShownOnCard}
            onChange={(e) => {
              setFieldConfig({
                ...fieldConfig,
                isShownOnCard: e.target.checked,
              });
            }}
            className="mb-2"
          />
          {fieldType !== 'label' && fieldType !== 'address' && (
            <Form.Check
              type="checkbox"
              label="Use in Coverage"
              checked={useInCoverage}
              onChange={(e) => setUseInCoverage(e.target.checked)}
              className="mb-2"
            />
          )}

          <br/><br/>
        </>
      )}
      
      <ConditionEditor
        value={condition}
        onChange={setCondition}
        allFields={forms.flatMap(f =>
          f.fields.map(field => ({
            ...field,
            label: `${f.name} - ${field.label}`
          }))
        )}
      />

      <Button type="submit">
        {!!editingFieldId ? 'Update Field' : 'Add Field'}
      </Button>

      {!!editingFieldId && (
        <Button variant="secondary" className="ms-2" onClick={resetFieldForm}>
          Cancel Edit
        </Button>
      )}
    </Form>
  );
};

export default FormField;
